import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import {
  CHECKLIST_ITEMS,
  CHECKLIST_ITEMS_LCV_EXTRA,
  CHECK_ANSWER_LABEL,
  CONDITION_CHECK_ITEMS,
  DAMAGE_AREA_LABEL,
  DAMAGE_KIND_CODE,
  DAMAGE_KIND_LABEL,
  DAMAGE_SEVERITY_LABEL,
  PHOTO_KIND_LABEL,
  SCHEMATICS,
  SIGNATURE_ROLE_LABEL,
  type CheckAnswer,
  type DamageArea,
  type DamageKind,
  type DamageSeverity,
  type PhotoKind,
  type SignatureRole,
  type VehicleCategory,
} from '@apleona/shared';
import { api, fileUrl, schematicUrl } from '../api';
import { useAssetTicket } from '../useAssetTicket';
import { Card, Empty, KV, Loading, Plate, StatusBadge, TypeBadge, formatDateTime, formatKm } from '../components/ui';

const ALL_ITEMS = [...CHECKLIST_ITEMS, ...CHECKLIST_ITEMS_LCV_EXTRA];

export const ProtokollDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [resendTo, setResendTo] = useState('');
  const [msg, setMsg] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const ticket = useAssetTicket();

  const { data: p, isLoading } = useQuery({
    queryKey: ['protokoll', id],
    queryFn: () => api<any>(`/protokolle/${id}`),
    enabled: !!id,
  });

  const resend = useMutation({
    mutationFn: (additional: string[]) =>
      api<any>(`/protokolle/${id}/resend`, { method: 'POST', body: JSON.stringify({ additional }) }),
    onSuccess: (r) => {
      setMsg(
        r.mail?.ok
          ? { tone: 'ok', text: `Erneut versendet an: ${r.recipients.to.join(', ')}` }
          : { tone: 'error', text: `Versand fehlgeschlagen: ${r.mail?.error ?? 'unbekannt'}` }
      );
      setResendTo('');
      void qc.invalidateQueries({ queryKey: ['protokoll', id] });
    },
    onError: (e: Error) => setMsg({ tone: 'error', text: e.message }),
  });

  const schematics = useMemo(
    () => (p ? SCHEMATICS[p.vehicle.category as VehicleCategory] ?? [] : []),
    [p]
  );

  if (isLoading || !p) return <Loading />;

  const answers = new Map<string, { value: string; note: string | null }>(
    p.checklistAnswers.map((a: any) => [a.itemKey, a])
  );
  const usedSchematics = schematics.filter(
    (s) => p.damages.some((d: any) => d.schematicId === s.id) || schematics.length === 1
  );
  const conditionAnswers: Record<string, boolean> = p.payload?.conditionAnswers ?? {};

  return (
    <div className="grid" style={{ gap: 16 }}>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <div className="row">
          <TypeBadge type={p.type} />
          <span className="mono" style={{ fontSize: 16, fontWeight: 700 }}>{p.number}</span>
          <StatusBadge status={p.status} />
        </div>
        <div className="row">
          <a
            className="btn"
            href={`/api/protokolle/${p.id}/pdf${ticket ? `?t=${encodeURIComponent(ticket)}` : ''}`}
            target="_blank"
            rel="noreferrer"
            aria-disabled={!ticket}
            onClick={(e) => {
              // Ohne Ticket wuerde der neue Tab ohne Authorization-Header laufen.
              if (!ticket) e.preventDefault();
            }}
          >
            {ticket ? 'PDF öffnen' : 'PDF wird vorbereitet …'}
          </a>
          <Link className="btn" to="/protokolle">Zurück zur Liste</Link>
        </div>
      </div>

      {msg ? <div className={`alert ${msg.tone === 'ok' ? 'ok' : 'error'}`}>{msg.text}</div> : null}

      <div className="grid k3">
        <Card title="Fahrzeug" bodyless>
          <KV
            rows={[
              ['Kennzeichen', <Link key="p" to={`/fahrzeuge/${p.vehicle.id}`}><Plate value={p.vehicle.plate} /></Link>],
              ['Marke/Modell', [p.vehicle.make, p.vehicle.model].filter(Boolean).join(' ') || '—'],
              ['FIN', p.vehicle.vin ?? '—'],
              ['Klasse', p.vehicle.category],
              ['km-Stand', formatKm(p.mileage)],
            ]}
          />
        </Card>
        <Card title="Vorgang" bodyless>
          <KV
            rows={[
              ['Zeitpunkt', formatDateTime(p.occurredAt)],
              ['Ort', p.place ?? '—'],
              ['Standort', p.location?.name ?? '—'],
              ['Erfasst von', `${p.coordinator.firstName} ${p.coordinator.lastName}`],
              ['Gerät', p.deviceName ?? '—'],
              ...(p.lat != null
                ? ([['GPS', `${p.lat.toFixed(5)}, ${p.lng?.toFixed(5)}`]] as [string, React.ReactNode][])
                : []),
            ]}
          />
        </Card>
        <Card title="Beteiligte" bodyless>
          <KV
            rows={[
              [
                'Neue*r Nutzer*in',
                p.newUser ? `${p.newUser.firstName ?? ''} ${p.newUser.lastName}`.trim() : '—',
              ],
              [
                'Bisherige*r Nutzer*in',
                p.previousUser ? `${p.previousUser.firstName ?? ''} ${p.previousUser.lastName}`.trim() : '—',
              ],
              ['Fahrer (Pool)', p.payload?.driverName ?? '—'],
              ['Zweck', p.payload?.purpose ?? '—'],
              ['E-Mail Nutzer', p.newUser?.email ?? p.previousUser?.email ?? '—'],
            ]}
          />
        </Card>
      </div>

      {p.damages.length > 0 || usedSchematics.length > 0 ? (
        <Card title={`Schäden (${p.damages.length})`}>
          <div className="row" style={{ alignItems: 'flex-start', gap: 20 }}>
            {usedSchematics.map((s) => (
              <div key={s.id}>
                <div className="small muted" style={{ marginBottom: 5 }}>{s.label}</div>
                <div className="schematic">
                  <img src={schematicUrl(s.file)} alt={s.label} />
                  {p.damages
                    .filter((d: any) => d.schematicId === s.id)
                    .map((d: any) => (
                      <span
                        key={d.id}
                        className={`mk ${d.preexisting ? 'pre' : ''}`}
                        style={{ left: `${d.x * 100}%`, top: `${d.y * 100}%` }}
                        title={`${DAMAGE_KIND_LABEL[d.kind as DamageKind]} – ${d.note ?? ''}`}
                      >
                        {d.index}
                      </span>
                    ))}
                </div>
              </div>
            ))}
          </div>

          {p.damages.length > 0 ? (
            <div className="table-wrap" style={{ marginTop: 14 }}>
              <table>
                <thead>
                  <tr>
                    <th className="num">Nr.</th>
                    <th>Art</th>
                    <th>Bereich</th>
                    <th>Schwere</th>
                    <th className="num">Größe</th>
                    <th>Bemerkung</th>
                  </tr>
                </thead>
                <tbody>
                  {p.damages.map((d: any) => (
                    <tr key={d.id}>
                      <td className="num">{d.index}</td>
                      <td>
                        <b>{DAMAGE_KIND_CODE[d.kind as DamageKind]}</b> ·{' '}
                        {DAMAGE_KIND_LABEL[d.kind as DamageKind]}
                      </td>
                      <td>{d.area ? DAMAGE_AREA_LABEL[d.area as DamageArea] : '—'}</td>
                      <td>{DAMAGE_SEVERITY_LABEL[d.severity as DamageSeverity]}</td>
                      <td className="num">{d.sizeMm ? `${d.sizeMm} mm` : '—'}</td>
                      <td>
                        {d.preexisting ? <span className="badge grey">Vorschaden</span> : null} {d.note ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="muted small" style={{ marginTop: 10 }}>Keine Schäden erfasst.</div>
          )}
        </Card>
      ) : null}

      {p.checklistAnswers.length > 0 ? (
        <Card title="Ausstattung & Unterlagen" bodyless>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Position</th><th>Antwort</th><th>Bemerkung</th></tr>
              </thead>
              <tbody>
                {ALL_ITEMS.filter((i) => answers.has(i.key)).map((i) => {
                  const a = answers.get(i.key)!;
                  return (
                    <tr key={i.key}>
                      <td>{i.label}</td>
                      <td>
                        <span
                          className={`badge ${a.value === 'JA' ? 'green' : a.value === 'NEIN' ? 'red' : 'grey'}`}
                        >
                          {CHECK_ANSWER_LABEL[a.value as CheckAnswer]}
                        </span>
                      </td>
                      <td className="small">{a.note ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}

      {Object.keys(conditionAnswers).length > 0 ? (
        <Card title="Sichtprüfung">
          <div className="grid k2">
            <div className="table-wrap">
              <table>
                <tbody>
                  {CONDITION_CHECK_ITEMS.filter((i) => i.key in conditionAnswers).map((i) => (
                    <tr key={i.key}>
                      <td>{i.label}</td>
                      <td style={{ width: 120 }}>
                        <span className={`badge ${conditionAnswers[i.key] ? 'green' : 'red'}`}>
                          {conditionAnswers[i.key] ? 'in Ordnung' : 'Mangel'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div>
              <h3>Mängelbeschreibung</h3>
              <p className="muted" style={{ whiteSpace: 'pre-wrap' }}>{p.payload?.defects ?? '—'}</p>
              {p.payload?.fuelLevelPercent != null ? (
                <p className="small muted">Tank-/Ladestand: {p.payload.fuelLevelPercent} %</p>
              ) : null}
            </div>
          </div>
        </Card>
      ) : null}

      {p.notes ? (
        <Card title="Bemerkungen">
          <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{p.notes}</p>
          {p.payload?.interiorSmell ? (
            <p className="small muted" style={{ marginBottom: 0 }}>
              Zustand innen / Geruchsbelastung: {p.payload.interiorSmell}
            </p>
          ) : null}
        </Card>
      ) : null}

      <Card title={`Fotodokumentation (${p.photos.length})`}>
        {p.photos.length === 0 ? (
          <Empty icon="▣" text="Keine Fotos vorhanden" />
        ) : !ticket ? (
          <Loading text="Fotos werden geladen …" />
        ) : (
          <div className="photo-grid">
            {p.photos.map((ph: any) => (
              <figure key={ph.id}>
                <a href={fileUrl(ph.filePath, ticket)} target="_blank" rel="noreferrer">
                  <img src={fileUrl(ph.thumbPath ?? ph.filePath, ticket)} alt={ph.kind} loading="lazy" />
                </a>
                <figcaption>
                  {PHOTO_KIND_LABEL[ph.kind as PhotoKind] ?? ph.kind}
                  {ph.note ? ` – ${ph.note}` : ''}
                </figcaption>
              </figure>
            ))}
          </div>
        )}
      </Card>

      <div className="grid k2">
        <Card title={`Unterschriften (${p.signatures.length})`}>
          {p.signatures.length === 0 ? (
            <Empty icon="✎" text="Keine Unterschriften erfasst" />
          ) : !ticket ? (
            <Loading text="Unterschriften werden geladen …" />
          ) : (
            <div className="grid k2">
              {p.signatures.map((s: any) => (
                <div key={s.id}>
                  <img
                    src={fileUrl(s.filePath, ticket)}
                    alt={s.name}
                    style={{ height: 62, objectFit: 'contain', display: 'block' }}
                  />
                  <div style={{ borderTop: '1px solid #101828', paddingTop: 4 }}>
                    <b>{s.name}</b>
                    <div className="small muted">
                      {SIGNATURE_ROLE_LABEL[s.role as SignatureRole]} · {formatDateTime(s.signedAt)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card title="E-Mail-Versand">
          {p.mailLogs.length === 0 ? (
            <Empty icon="✉" text="Noch kein Versand protokolliert" />
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Zeitpunkt</th><th>Empfänger</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {p.mailLogs.map((m: any) => (
                    <tr key={m.id}>
                      <td className="small">{formatDateTime(m.createdAt)}</td>
                      <td className="small">
                        {m.to}
                        {m.cc ? <div className="muted">CC: {m.cc}</div> : null}
                      </td>
                      <td>
                        <span className={`badge ${m.status === 'GESENDET' ? 'green' : m.status === 'FEHLER' ? 'red' : 'grey'}`}>
                          {m.status}
                        </span>
                        {m.error ? <div className="small muted">{m.error}</div> : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="toolbar" style={{ marginTop: 14 }}>
            <div className="field grow">
              <label>Zusätzliche Empfänger (Komma-getrennt)</label>
              <input
                value={resendTo}
                onChange={(e) => setResendTo(e.target.value)}
                placeholder="name@apleona.com, weitere@apleona.com"
              />
            </div>
            <button
              className="btn primary"
              disabled={resend.isPending}
              onClick={() =>
                resend.mutate(
                  resendTo.split(',').map((s) => s.trim()).filter((s) => s.includes('@'))
                )
              }
            >
              {resend.isPending ? 'Sende …' : 'Erneut versenden'}
            </button>
          </div>
        </Card>
      </div>
    </div>
  );
};
